import { useCallback, useEffect, useState } from 'react'
import { API_BASE_URL } from '../utils/apiBaseUrl'
import { apiFetch } from '../utils/apiFetch'
import { invalidatePayPeriodCache } from '../services/payPeriodService'
import {
  DEFAULT_OPERATION_CONFIG,
  normalizeOperationConfig,
  type ControlCenterOperationConfig,
} from '../utils/controlCenterOperationConfig'

let cachedConfig: ControlCenterOperationConfig | null = null
let inflight: Promise<ControlCenterOperationConfig> | null = null

async function fetchOperationConfig(): Promise<ControlCenterOperationConfig> {
  if (cachedConfig) return cachedConfig
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const token = localStorage.getItem('lts_jwt_token')
      const res = await apiFetch(`${API_BASE_URL}/control-center/operation-config`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok || !payload?.success) {
        const fallback = { ...DEFAULT_OPERATION_CONFIG }
        cachedConfig = fallback
        return fallback
      }
      const next = normalizeOperationConfig({ ...DEFAULT_OPERATION_CONFIG, ...payload.data })
      cachedConfig = next
      return next
    } catch {
      const fallback = { ...DEFAULT_OPERATION_CONFIG }
      cachedConfig = fallback
      return fallback
    } finally {
      inflight = null
    }
  })()
  return inflight
}

export function invalidateOperationConfigCache() {
  cachedConfig = null
}

export function useControlCenterOperationConfig() {
  const [config, setConfig] = useState<ControlCenterOperationConfig>(
    () => cachedConfig ?? DEFAULT_OPERATION_CONFIG,
  )
  const [loading, setLoading] = useState(!cachedConfig)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    invalidateOperationConfigCache()
    const next = await fetchOperationConfig()
    setConfig(next)
    setLoading(false)
    return next
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const next = await fetchOperationConfig()
      if (!cancelled) {
        setConfig(next)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const save = useCallback(async (partial: Partial<ControlCenterOperationConfig>) => {
    const token = localStorage.getItem('lts_jwt_token')
    const res = await apiFetch(`${API_BASE_URL}/control-center/operation-config`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(partial),
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok || !payload?.success) {
      const message = payload?.message || 'Failed to save operation config'
      setError(message)
      throw new Error(message)
    }
    const next = normalizeOperationConfig({ ...DEFAULT_OPERATION_CONFIG, ...payload.data })
    cachedConfig = next
    invalidatePayPeriodCache()
    setConfig(next)
    setError(null)
    return next
  }, [])

  return { config, loading, error, refresh, save }
}
